import { differenceInHours } from 'date-fns';
import type { InteractionEnvelope } from '@/common/types/interaction';
import { formatAddress } from '@/common/utils/address';
import { getFullName } from '@/common/utils/person';
import type { CoreRequest } from '$api-clients/hooks';

type EnvelopeInteraction =
  CoreRequest<'/interactions'>['Data']['results'][number]['interaction'];

export function getEnvelopeDetails(
  envelopes: Pick<InteractionEnvelope, 'sentAt'>[],
) {
  if (!envelopes.length) return '';

  const envelopeCountMessage = `${envelopes.length} envelope${envelopes.length > 1 ? 's' : ''} awaiting signature`;

  const sentAtTimes = envelopes.map((envelope) =>
    envelope.sentAt ? new Date(envelope.sentAt).getTime() : Infinity,
  );

  const minSentAtTime = Math.min(...sentAtTimes);
  if (minSentAtTime === Infinity) return envelopeCountMessage;

  const sentAtDate = new Date(minSentAtTime);
  const numDaysAgo = Math.floor(differenceInHours(Date.now(), sentAtDate) / 24);

  return `${envelopeCountMessage}, sent ${numDaysAgo} day${numDaysAgo > 1 ? 's' : ''} ago`;
}

export function getInteractionHeader(interaction: EnvelopeInteraction) {
  if (interaction.type === 'referral') {
    const agent = interaction.contacts.find(
      (contact) => contact.type === 'agent',
    );
    const agentName = getFullName(agent?.firstName, agent?.lastName);

    if (interaction.referralType === 'receiving') {
      return `Referral from ${agentName}`;
    }

    return `Referral to ${agentName}`;
  }

  if (interaction.type === 'clientGroup') {
    return 'Buyer Agreement';
  }

  if (interaction.type === 'listing') {
    return formatAddress(interaction.property, { includeStreet2: true });
  }

  // Offers and leases surface the address of their linked listing.
  return formatAddress(interaction.listing?.property, {
    includeStreet2: true,
  });
}
